import { Form, Spin } from 'antd';
import { Content } from 'antd/es/layout/layout';
import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { SCard } from 'app/components/StaycationUI';
import { SDetailLayout } from 'app/components/StaycationUI/SDetailLayout/SDetailLayout';
import { AssetDetailTitle } from 'app/components/commons/AssetDetailTitle/AssetDetailTitle';
import { CONTACTS_SECTIONS } from 'app/components/commons/Hotel/Contacts/ContactSection';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotelContacts } from 'app/hooks/data/core/admin/hotels/useGetHotelContacts';
import { useUpdateHotelContacts } from 'app/hooks/data/core/admin/hotels/useUpdateHotelContacts';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import type { AdminHotelContacts } from 'app/typings/core/admin/hotel/contacts';
import { SCROLL_TO_FIRST_ERROR } from 'app/utils/scroll';

import type { ContactsProps } from '../../../../commons/Hotel/Contacts/Contacts.types';

import styles from './Contacts.module.scss';

const ContactsComponent = ({
  headerMenu,
  headerExtra,
  hotelId,
}: ContactsProps) => {
  const [form] = Form.useForm<AdminHotelContacts>();

  const [, forceUpdate] = useReducer((count: number) => count + 1, 0);

  const { enterEditMode, enterViewMode, mode, isViewMode } =
    useContext(ViewEditModeContext);

  const { search } = useLocation();

  const { data: hotel } = useGetHotel(hotelId);
  const { data: hotelContacts, isLoading: isHotelContactsLoading } =
    useHotelContacts(hotelId);
  const { mutateAsync: updatedHotelContacts } = useUpdateHotelContacts(hotelId);

  const [isLoading, setIsLoading] = useState(false);

  const onSave = useCallback(() => {
    setIsLoading(true);
    form.submit();
  }, [form]);

  const onCancel = useCallback(() => {
    enterViewMode();
    form.resetFields();

    if (hotelContacts) {
      form.setFieldsValue(hotelContacts);
    }
  }, [enterViewMode, form, hotelContacts]);

  const onFinish = useCallback(
    (values: AdminHotelContacts) => {
      updatedHotelContacts(values).finally(() => {
        setIsLoading(false);
      });

      enterViewMode();
    },
    [updatedHotelContacts, enterViewMode]
  );

  useEffect(() => {
    if (hotelContacts) {
      form.setFieldsValue(hotelContacts);
    }
  }, [hotelContacts, form]);

  useEffect(() => {
    if (isHotelContactsLoading) {
      return undefined;
    }

    const section = new URLSearchParams(search).get('section');

    if (!section) {
      return undefined;
    }

    let innerFrame = 0;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => {
        document
          .getElementById(section)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, [search, isHotelContactsLoading]);

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFieldsChange={forceUpdate}
      scrollToFirstError={SCROLL_TO_FIRST_ERROR}
    >
      <SDetailLayout
        title={
          <AssetDetailTitle
            assetTitle={hotel?.name ?? `Hôtel #${hotelId}`}
            hotelName={hotel?.name}
            isNewDataModel={!hotel?.isLegacy}
          />
        }
        backRoute="/hotels"
        mode={mode}
        onEdit={enterEditMode}
        onSave={onSave}
        onCancel={onCancel}
        reset={() => {
          /* no-op */
        }}
        headerMenu={headerMenu}
        headerExtra={headerExtra}
        isDirty={form.isFieldsTouched()}
        isValid={form
          .getFieldsError()
          .every((item) => item.errors.length === 0)}
        disabled={isLoading}
        saveDisabled={form
          .getFieldsError()
          .some((item) => item.errors.length > 0)}
        isLoading={isLoading}
      >
        <Spin
          spinning={isHotelContactsLoading}
          wrapperClassName={styles.mainContent}
        >
          <Content className="hotel-detail__content">
            {CONTACTS_SECTIONS.map((section) => {
              const SectionComponent = section.component;

              return (
                <SCard
                  key={section.key}
                  id={section.key}
                  title={section.title}
                  subtitle={section.subtitle}
                  className={styles.contactSection}
                >
                  <SectionComponent
                    isEditMode={!isViewMode}
                    hotelId={hotelId}
                    form={form}
                  />
                </SCard>
              );
            })}
            <div className={styles.bottomSpacer} />
          </Content>
        </Spin>
      </SDetailLayout>
    </Form>
  );
};

export const Contacts = ({
  headerMenu,
  headerExtra,
  hotelId,
}: ContactsProps) => {
  return (
    <ViewEditModeContextProvider>
      <ContactsComponent
        headerMenu={headerMenu}
        headerExtra={headerExtra}
        hotelId={hotelId}
      />
    </ViewEditModeContextProvider>
  );
};
